import type { BillingStatus, PaymentMethod, PaymentStatus, Payment, ScheduleBillingInfo, Billing } from './types';

/**
 * 정산 상태 라벨 반환
 */
export function getBillingStatusLabel(status: BillingStatus): string {
    const labels: Record<BillingStatus, string> = {
        UNSETTLED: '미정산',
        PARTIALLY_SETTLED: '부분정산',
        SETTLED: '정산완료',
        PARTIALLY_REFUNDED: '부분환불',
        FULLY_REFUNDED: '전체환불',
    };
    return labels[status];
}

/**
 * 정산 상태별 스타일 반환
 */
export function getBillingStatusStyle(status: BillingStatus): { bg: string; text: string; emoji: string } {
    const styles: Record<BillingStatus, { bg: string; text: string; emoji: string }> = {
        UNSETTLED: { bg: 'bg-gray-100', text: 'text-gray-700', emoji: '⚪' },
        PARTIALLY_SETTLED: { bg: 'bg-yellow-100', text: 'text-yellow-700', emoji: '🟡' },
        SETTLED: { bg: 'bg-green-100', text: 'text-green-700', emoji: '🟢' },
        PARTIALLY_REFUNDED: { bg: 'bg-orange-100', text: 'text-orange-700', emoji: '🟠' },
        FULLY_REFUNDED: { bg: 'bg-red-100', text: 'text-red-700', emoji: '🔴' },
    };
    return styles[status];
}

/**
 * 결제 상태 라벨 반환
 */
export function getPaymentStatusLabel(status: PaymentStatus): string {
    const labels: Record<PaymentStatus, string> = {
        PENDING: '미결제',
        COMPLETED: '결제완료',
        PARTIALLY_REFUNDED: '부분환불',
        REFUNDED: '전액환불',
    };
    return labels[status];
}

/**
 * 결제 상태별 스타일 반환
 */
export function getPaymentStatusStyle(status: PaymentStatus): { bg: string; text: string } {
    const styles: Record<PaymentStatus, { bg: string; text: string }> = {
        PENDING: { bg: 'bg-yellow-100', text: 'text-yellow-700' },
        COMPLETED: { bg: 'bg-green-100', text: 'text-green-700' },
        PARTIALLY_REFUNDED: { bg: 'bg-orange-100', text: 'text-orange-700' },
        REFUNDED: { bg: 'bg-red-100', text: 'text-red-700' },
    };
    return styles[status];
}

/**
 * 결제 방법 라벨 반환
 */
export function getPaymentMethodLabel(method: PaymentMethod): string {
    const labels: Record<PaymentMethod, string> = {
        CASH: '현금',
        CARD: '카드',
        TRANSFER: '계좌이체',
        OTHER: '기타',
    };
    return labels[method];
}

/**
 * 금액 포맷팅
 */
export function formatAmount(amount: number): string {
    return amount.toLocaleString('ko-KR') + '원';
}

/**
 * 날짜 포맷팅 (UTC → KST +9시간 변환)
 */
export function formatDateTime(dateString: string): string {
    const date = new Date(dateString);
    const kstDate = new Date(date.getTime() + 9 * 60 * 60 * 1000);
    return kstDate.toLocaleString('ko-KR', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        timeZone: 'UTC',
    });
}

/**
 * 이용 시간 계산 (분 단위)
 */
export function calculateUsageMinutes(startAt: string, endAt: string): number {
    const start = new Date(startAt);
    const end = new Date(endAt);
    return Math.floor((end.getTime() - start.getTime()) / (1000 * 60));
}

/**
 * 이용 시간 포맷팅
 */
export function formatUsageDuration(minutes: number): string {
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    if (hours > 0 && mins > 0) {
        return `${hours}시간 ${mins}분`;
    } else if (hours > 0) {
        return `${hours}시간`;
    } else {
        return `${mins}분`;
    }
}

/**
 * 정산 상태 자동 계산
 */
export function calculateBillingStatus(
    finalAmount: number,
    totalPaidAmount: number,
    totalRefundedAmount: number
): BillingStatus {
    const netPaidAmount = totalPaidAmount - totalRefundedAmount;

    if (totalRefundedAmount > 0 && netPaidAmount <= 0) {
        return 'FULLY_REFUNDED';
    }
    if (totalRefundedAmount > 0 && netPaidAmount > 0) {
        return 'PARTIALLY_REFUNDED';
    }
    if (netPaidAmount >= finalAmount) {
        return 'SETTLED';
    }
    if (netPaidAmount > 0) {
        return 'PARTIALLY_SETTLED';
    }
    return 'UNSETTLED';
}

// 정산 상태 필터 옵션
export const BILLING_STATUS_OPTIONS: { value: BillingStatus | 'ALL'; label: string }[] = [
    { value: 'ALL', label: '전체' },
    { value: 'UNSETTLED', label: '미정산' },
    { value: 'PARTIALLY_SETTLED', label: '부분정산' },
    { value: 'SETTLED', label: '정산완료' },
    { value: 'PARTIALLY_REFUNDED', label: '부분환불' },
    { value: 'FULLY_REFUNDED', label: '전체환불' },
];

// 결제 방법 필터 옵션
export const PAYMENT_METHOD_OPTIONS: { value: PaymentMethod | 'ALL'; label: string }[] = [
    { value: 'ALL', label: '전체' },
    { value: 'CARD', label: '카드' },
    { value: 'CASH', label: '현금' },
    { value: 'TRANSFER', label: '계좌이체' },
    { value: 'OTHER', label: '기타' },
];

// ===== Deprecated stub functions (for backwards compatibility) =====
// These functions previously accessed mock data and should be replaced with actual API calls

/**
 * @deprecated Use API to fetch schedule billing info
 */
export function getScheduleBillingInfo(_scheduleId: number): ScheduleBillingInfo {
    return { isSettled: false };
}

/** @deprecated Use getScheduleBillingInfo instead */
export const getScheduleSettlementInfo = getScheduleBillingInfo;

/**
 * @deprecated Use API to fetch billing by schedule ID
 */
export function getBillingByScheduleId(_scheduleId: number): Billing | null {
    return null;
}

/** @deprecated Use getBillingByScheduleId instead */
export const getSettlementByScheduleId = getBillingByScheduleId;

/**
 * @deprecated Use API to fetch member payments
 */
export function getMemberPayments(_memberId: number): Payment[] {
    return [];
}

/**
 * @deprecated Use API to fetch member payments by name
 */
export function getMemberPaymentsByName(_memberName: string): Payment[] {
    return [];
}
